import { useEffect, useRef } from "react";
import { Link } from "react-router";
import { useTranslation } from "react-i18next";
import { AppBrandLogo } from "@/components/brand/AppBrandLogo";
import { IubendaLink } from "@/components/legal/IubendaLink";
import { IubendaCookiePreferencesLink } from "@/components/legal/IubendaCookiePreferencesLink";
import { LanguageSwitcher } from "@/i18n/components/LanguageSwitcher";
import { IUBENDA_SCRIPT_ID, IUBENDA_SCRIPT_SRC } from "@/constants/iubenda";
import { useAuthStore } from "@/store/authStore";
import { buttonVariants } from "@/components/ui";
import { ArrowLeft, ShieldCheck } from "lucide-react";

/**
 * The Iubenda embed script resets `.iubenda-embed` anchors (padding, border,
 * background), so the anchor stays bare (`!` overrides) and the pill styling
 * lives on an inner span.
 */
const POLICY_LINK_CLASS =
  "group !m-0 !inline-block !border-0 !bg-transparent !p-0 !text-[13px] !font-medium !text-surface-700 !no-underline";
const POLICY_PILL_CLASS =
  "inline-flex h-8 items-center rounded-lg border border-surface-200 bg-surface-0 px-3 shadow-xs transition-colors group-hover:border-surface-300 group-hover:bg-surface-50 group-hover:text-surface-900 dark:group-hover:bg-surface-100";

export default function PrivacyPolicyPage() {
  const { t } = useTranslation("common");
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const backTarget = isAuthenticated ? "/" : "/login";
  const privacyLinkRef = useRef<HTMLAnchorElement>(null);

  useEffect(() => {
    let cancelled = false;

    const openPolicy = () => {
      if (!cancelled) {
        privacyLinkRef.current?.click();
      }
    };

    const scheduleOpen = () => {
      window.setTimeout(openPolicy, 300);
    };

    const existingScript = document.getElementById(IUBENDA_SCRIPT_ID);
    if (existingScript) {
      scheduleOpen();
      return () => {
        cancelled = true;
      };
    }

    const script = document.createElement("script");
    script.id = IUBENDA_SCRIPT_ID;
    script.src = IUBENDA_SCRIPT_SRC;
    script.async = true;
    script.addEventListener("load", scheduleOpen);
    document.body.appendChild(script);

    return () => {
      cancelled = true;
      script.removeEventListener("load", scheduleOpen);
    };
  }, []);

  return (
    <div className="min-h-screen bg-surface-50">
      <header className="border-b border-surface-200 bg-surface-0">
        <div className="mx-auto flex h-14 max-w-6xl items-center justify-between gap-4 px-4 sm:px-6 lg:px-8">
          <Link to={backTarget} className="block h-8 w-24" aria-label="Flitrip">
            <AppBrandLogo
              background="light"
              className="h-full w-full"
              imgClassName="object-left"
            />
          </Link>
          <div className="flex items-center gap-2">
            <LanguageSwitcher variant="sidebar" />
            <Link
              to={backTarget}
              className={buttonVariants({ variant: "outline", size: "sm" })}
            >
              <ArrowLeft className="mr-1.5 h-4 w-4" aria-hidden />
              {isAuthenticated ? t("backToDashboard") : t("backToLogin")}
            </Link>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-2xl px-4 py-16 sm:px-6">
        <div className="rounded-xl border border-surface-200 bg-surface-0 px-6 py-8 text-center shadow-card">
          <span className="mx-auto flex h-10 w-10 items-center justify-center rounded-lg bg-primary-50 text-primary-600 ring-1 ring-inset ring-primary-100">
            <ShieldCheck className="h-5 w-5" aria-hidden />
          </span>
          <p className="mt-4 text-sm text-surface-600">
            {t("privacyPolicyOpening")}
          </p>
          <div className="mt-6 flex flex-wrap items-center justify-center gap-2">
            <IubendaLink
              ref={privacyLinkRef}
              policy="privacy"
              className={POLICY_LINK_CLASS}
              title={t("privacyPolicy")}
            >
              <span className={POLICY_PILL_CLASS}>{t("privacyPolicy")}</span>
            </IubendaLink>
            <IubendaLink
              policy="cookie"
              className={POLICY_LINK_CLASS}
              title={t("cookiePolicy")}
            >
              <span className={POLICY_PILL_CLASS}>{t("cookiePolicy")}</span>
            </IubendaLink>
            <IubendaCookiePreferencesLink className={POLICY_LINK_CLASS}>
              <span className={POLICY_PILL_CLASS}>{t("cookiePreferences")}</span>
            </IubendaCookiePreferencesLink>
          </div>
        </div>
        <p className="mt-8 text-center text-xs text-surface-500">
          {t("copyright", { year: new Date().getFullYear() })}
        </p>
      </main>
    </div>
  );
}
